import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { api, imgUrl } from "../lib/api";
import { SectionHead } from "./Reveal";
import { EASE } from "../lib/site";

export const CATS = [["tutti", "Tutti"], ["microblading", "Microblading"], ["unghie", "Unghie"]];
export const catLabel = (c) => (CATS.find(([k]) => k === c) || [c, c])[1];

const SAMPLES = [
  { id: "s1", category: "unghie", caption: "Nail art", url: "/img/nails3.jpg" },
  { id: "s2", category: "microblading", caption: "Microblading", url: "/img/micro1.jpg" },
  { id: "s3", category: "unghie", caption: "Ricostruzione", url: "/img/nails2.jpg" },
  { id: "s4", category: "microblading", caption: "Sopracciglia", url: "/img/micro2.jpg" },
  { id: "s5", category: "unghie", caption: "Atelier", url: "/img/studio2.jpg" },
];

const Tile = ({ p, i, big, onOpen }) => (
  <motion.button
    layout
    data-testid={`gallery-item-${p.id}`}
    onClick={() => onOpen(p)}
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.8, delay: (i % 4) * 0.06, ease: EASE }}
    className={`group relative overflow-hidden rounded-[1.5rem] bg-[var(--blush)] ${big ? "col-span-2 row-span-2" : ""}`}
  >
    <img src={p.sample ? p.url : imgUrl(p.url)} alt={p.caption || catLabel(p.category)} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-[50%_35%] transition-transform duration-[1.2s] ease-out group-hover:scale-[1.07]" />
    <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/70 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
    <div className="absolute bottom-0 left-0 translate-y-4 p-5 text-left opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-y-0 group-hover:opacity-100">
      <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--nude)]">{catLabel(p.category)}</p>
      {p.caption && <p className="font-display text-lg italic text-white">{p.caption}</p>}
    </div>
  </motion.button>
);

export const Gallery = () => {
  const [cat, setCat] = useState("tutti");
  const [open, setOpen] = useState(null);
  const { data, isSuccess } = useQuery({ queryKey: ["gallery"], queryFn: async () => (await api.get("/gallery")).data });
  const real = Array.isArray(data) ? data : [];
  const usingSamples = isSuccess && real.length === 0 ? true : !isSuccess;
  const all = usingSamples ? SAMPLES.map((s) => ({ ...s, sample: true })) : real;
  const items = cat === "tutti" ? all : all.filter((p) => p.category === cat);

  return (
    <section id="lavori" data-testid="gallery-section" className="py-24 md:py-36">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col justify-between gap-10 lg:flex-row lg:items-end">
          <SectionHead num="03" eyebrow="Portfolio" title={<>I nostri <em className="gold-text italic">lavori</em></>} />
          <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
            {CATS.map(([k, l]) => (
              <button key={k} data-testid={`gallery-filter-${k}`} onClick={() => setCat(k)} className={`rounded-full border px-5 py-2.5 text-sm transition-colors duration-300 ${cat === k ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--cream)]" : "border-[var(--ink)]/20 hover:border-[var(--gold)]"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="mt-14 grid auto-rows-[170px] grid-flow-dense grid-cols-2 gap-3 md:auto-rows-[230px] md:grid-cols-4 md:gap-4">
          {items.map((p, i) => <Tile key={p.id} p={p} i={i} big={i % 5 === 0} onOpen={setOpen} />)}
        </div>
        {items.length === 0 && <p data-testid="gallery-empty" className="mt-10 text-[var(--ink-soft)]">Nessun lavoro in questa categoria, per ora.</p>}
        {usingSamples && <p data-testid="gallery-sample-note" className="mt-6 text-xs tracking-wide text-[var(--ink-soft)]">Immagini dimostrative.</p>}
      </div>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent data-testid="gallery-lightbox" className="max-w-3xl overflow-hidden border-0 bg-[var(--cream)] p-0">
          <DialogTitle className="sr-only">{open?.caption || "Lavoro"}</DialogTitle>
          {open && <img src={open.sample ? open.url : imgUrl(open.url)} alt={open.caption || ""} className="max-h-[80vh] w-full object-contain" />}
          {open && <div className="px-6 pb-5"><p className="eyebrow">{catLabel(open.category)}</p>{open.caption && <p className="font-display text-xl italic">{open.caption}</p>}</div>}
        </DialogContent>
      </Dialog>
    </section>
  );
};
